<script setup lang="ts">
import { RouterLinkBack, VButton, VIcon } from '@/modules/shared/components'
import { createReusableTemplate } from '@vueuse/core'
import { computed, onMounted, ref } from 'vue'
import { useInvestingInvestorStore } from '../stores/investor-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useRoute, useRouter } from 'vue-router'
import { useIndividualStore } from '../stores/better-individual-store'
import IndividualForm from '../components/individuals/individual-form.vue'
import DisbursementPreferencesImprovedForm from '../components/disbursement-preferences-improved-form.vue'
import IndividualAccreditationForm from '../components/individuals/individual-accreditation-form.vue'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { scrollToTop } from '../utils/scroll-to-section'
import { useAccreditationStore } from '@/modules/screening/stores/accreditation-store'

const [DefineTemplate, ReuseTemplate] = createReusableTemplate<{
  label: string
  isFirst: boolean
  isLast: boolean
  isDone: boolean
  isCurrent: boolean
}>()

const router = useRouter()

const workspaceStore = useWorkspaceStore()
const accreditationStore = useAccreditationStore()
const investorStore = useInvestingInvestorStore()
const individualStore = useIndividualStore()
const currentIndividual = ref(null)

const tabs = computed(() => {
  const allTabs = [
    {
      key: 'personal_information',
      name: 'Personal Information',
    },
    {
      key: 'accreditation',
      name: 'Accreditation',
      accessible: workspaceStore.current.options.is_accreditation_required,
    },
    {
      key: 'disbursement_preferences',
      name: 'Disbursement Preferences',
    },
  ]

  return allTabs.filter((tab) => {
    if (tab.accessible === undefined) return true // If no accessibility condition, include tab
    return tab.accessible // Only include tabs that are accessible
  })
})
const selectedTabKey: any = ref('personal_information')

const isTabActive = (tabKey: string) => selectedTabKey.value === tabKey

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { individual_id, slug } = useRoute().params as { individual_id: string; slug: string }
const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const fetch = async () => {
  await Promise.all([investorStore.fetchIndividual(individual_id)])
  currentIndividual.value = investorStore.items.get(`individual:${individual_id}`)
}

const incrementOnboardingStep = async (currentStep) => {
  await individualStore.incrementOnboardingStep(individual_id, currentStep, { slug })
}

const onSubmitPersonalInformation = async (payload: any) => {
  await individualStore.updatePersonalInformation(individual_id, payload, { slug })
  await incrementOnboardingStep('personal_information')
  await fetch()
}

const onSubmitAccreditation = async (payload: any) => {
  await accreditationStore.updateResponse(currentIndividual.value.accreditation.id, payload, { slug })
  await incrementOnboardingStep('accreditation')
  await fetch()
}

const onSubmitDisbursementPreferences = async (payload) => {
  await individualStore.updateDisbursementPreferences(individual_id, payload, { slug })
  await incrementOnboardingStep('disbursement_preferences')
  await fetch()
}

onMounted(async () => {
  showSkeleton()
  await Promise.all([workspaceStore.fetchCurrent(), fetch()])

  if (currentIndividual.value.onboarding_completed_at) {
    router.push({
      name: 'investing.individual.account',
      params: { individual_id, slug },
    })
  }

  selectedTabKey.value = currentIndividual.value.onboarding_current_step
  hideSkeleton()
})

///////////////////////////////////////////////////////////////////////////////
// Step by Step Bar
///////////////////////////////////////////////////////////////////////////////

const firstTab = computed(() => tabs.value[0])
const lastTab = computed(() => tabs.value[tabs.value.length - 1])
const isFirstTab = (tabKey: string) => tabKey === firstTab.value.key
const isLastTab = (tabKey: string) => tabKey === lastTab.value.key
const isDoneTab = (tabKey: string) => {
  const currentIndex = tabs.value.findIndex((tab) => tab.key === tabKey)
  return currentIndex < tabs.value.findIndex((tab) => tab.key === selectedTabKey.value)
}
const isCurrentTab = (tabKey: string) => tabKey === selectedTabKey.value

///////////////////////////////////////////////////////////////////////////////
// Personal Information From
///////////////////////////////////////////////////////////////////////////////

const personalInformationForm = ref(null)

///////////////////////////////////////////////////////////////////////////////
// Accreditation From
///////////////////////////////////////////////////////////////////////////////

const accreditationForm = ref(null)

///////////////////////////////////////////////////////////////////////////////
// Disbursement Preferences From
///////////////////////////////////////////////////////////////////////////////

const disbursementPreferencesForm = ref(null)

const disbursementPreferences = computed(() => {
  if (!currentIndividual.value) return null

  return {
    disbursement_method: currentIndividual.value.disbursement_method,
    disbursement_wire_bank_name: currentIndividual.value.disbursement_wire_bank_name,
    disbursement_wire_bank_address: currentIndividual.value.disbursement_wire_bank_address,
    disbursement_wire_bank_routing_number: currentIndividual.value.disbursement_wire_bank_routing_number,
    disbursement_wire_bank_account_number: currentIndividual.value.disbursement_wire_bank_account_number,
    disbursement_wire_bank_swift_code: currentIndividual.value.disbursement_wire_bank_swift_code,
    disbursement_wire_account_name: currentIndividual.value.disbursement_wire_account_name,
    disbursement_wire_for_further_credit_to: currentIndividual.value.disbursement_wire_for_further_credit_to,
    disbursement_check_receiver_name: currentIndividual.value.disbursement_check_receiver_name,
    disbursement_other_details: currentIndividual.value.disbursement_other_details,
  }
})

///////////////////////////////////////////////////////////////////////////////
// Bottom Nav
///////////////////////////////////////////////////////////////////////////////

const prevTab = computed(() => {
  const currentIndex = tabs.value.findIndex((tab) => tab.key === selectedTabKey.value)
  return currentIndex > 0 ? tabs.value[currentIndex - 1] : null
})
const nextTab = computed(() => {
  const currentIndex = tabs.value.findIndex((tab) => tab.key === selectedTabKey.value)
  return currentIndex < tabs.value.length - 1 ? tabs.value[currentIndex + 1] : null
})

const isBackVisible = computed(() => !!prevTab.value)
const isContinueVisible = computed(() => !!nextTab.value)
const isFinishVisible = computed(() => !nextTab.value)

const onClickContinue = async () => {
  let isValid = true

  switch (selectedTabKey.value) {
    case 'personal_information':
      await personalInformationForm.value?.submit()
      isValid = await personalInformationForm.value?.isValid()
      break
    case 'accreditation':
      await accreditationForm.value?.submit()
      isValid = await accreditationForm.value?.isValid()
      break
    case 'disbursement_preferences':
      await disbursementPreferencesForm.value?.submit()
      isValid = await disbursementPreferencesForm.value?.isValid()
      break
    default:
      return
  }

  if (!isValid) return
  selectedTabKey.value = currentIndividual.value?.onboarding_current_step || nextTab.value?.key
  scrollToTop('.top-section')
}

const onClickBack = () => {
  selectedTabKey.value = prevTab.value?.key
  scrollToTop('.top-section')
}

const onClickFinish = async () => {
  await disbursementPreferencesForm.value?.submit()
  router.push({
    name: 'investing.individual.account',
  })
}
</script>

<template>
  <DefineTemplate v-slot="{ label, isFirst, isLast, isDone, isCurrent }">
    <div class="flex min-w-0 flex-1 flex-col items-center sm:w-[140px] sm:flex-none md:w-[210px]">
      <div class="flex w-full items-center justify-center">
        <div
          class="-mr-[1px] h-1 flex-grow"
          :class="{
            'bg-[#3D8A9D]': (isDone && !isFirst) || isCurrent,
            'bg-transparent': isFirst,
            'bg-gray-300': !isDone && !isCurrent,
          }"
        ></div>
        <div
          class="flex h-5 w-5 items-center justify-center rounded-full"
          :class="{
            'bg-white': !isDone,
            'bg-[#3D8A9D]': isDone,
            'border-[5px]': !isDone,
            'border-[#3D8A9D]': isCurrent || isDone,
            'border-gray-300': !isCurrent && !isDone,
          }"
        >
          <VIcon name="Check" class="h-3.5 w-3.5 text-white" v-if="isDone" />
        </div>
        <div
          class="h-1 flex-grow"
          :class="{ 'bg-[#3D8A9D]': isDone && !isLast, 'bg-transparent': isLast, 'bg-gray-300': !isDone }"
        ></div>
      </div>
      <div
        class="w-full truncate text-center text-xs sm:text-sm md:text-base"
        :class="{ 'font-semibold text-[#3D8A9D]': isCurrent }"
      >
        {{ label }}
      </div>
    </div>
  </DefineTemplate>

  <div class="flex max-h-screen min-h-screen flex-col overflow-x-hidden">
    <div class="top-section flex-grow overflow-y-auto overflow-x-hidden">
      <div class="mt-10 px-4 sm:px-10 md:px-20 lg:px-40">
        <RouterLinkBack :to="{ name: 'dashboard' }" :fallback="{ name: 'dashboard' }">
          <div class="flex w-fit items-center gap-2 p-2">
            <VIcon name="ArrowNarrowLeft" />
            Dashboard
          </div>
        </RouterLinkBack>
      </div>

      <div class="mt-5 px-4 sm:px-10 md:px-20 lg:px-60">
        <div class="mb-10 text-center text-2xl font-semibold">Complete Your Profile</div>
        <div class="flex w-full items-center justify-center">
          <ReuseTemplate
            :label="tab.name"
            :isFirst="isFirstTab(tab.key)"
            :isLast="isLastTab(tab.key)"
            :isDone="isDoneTab(tab.key)"
            :isCurrent="isCurrentTab(tab.key)"
            v-for="tab in tabs"
          />
        </div>

        <div class="my-10 max-w-full">
          <div v-show="isTabActive('personal_information')">
            <IndividualForm
              ref="personalInformationForm"
              :individual="currentIndividual"
              :onSubmit="onSubmitPersonalInformation"
              :isWizard="true"
            />
          </div>
          <div v-show="isTabActive('accreditation')">
            <IndividualAccreditationForm
              ref="accreditationForm"
              :individual="currentIndividual"
              :onSubmit="onSubmitAccreditation"
              :isWizard="true"
            />
          </div>
          <div v-show="isTabActive('disbursement_preferences')">
            <DisbursementPreferencesImprovedForm
              ref="disbursementPreferencesForm"
              :disbursementPreferences="disbursementPreferences"
              :onSubmit="onSubmitDisbursementPreferences"
              :isWizard="true"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex h-fit items-center justify-between border-t bg-white/50 px-6 py-4 shadow-[0_-2px_4px_rgba(0,0,0,0.05)] backdrop-blur"
      v-if="!skeleton"
    >
      <div class="left flex items-center space-x-3"></div>
      <div class="right mr-0 flex items-center space-x-1.5 sm:mr-4 md:mr-16">
        <VButton :click="onClickBack" v-if="isBackVisible">Back</VButton>
        <VButton variant="primary" :click="onClickContinue" v-if="isContinueVisible">Continue</VButton>
        <VButton variant="primary" :click="onClickFinish" v-if="isFinishVisible">Finish</VButton>
      </div>
    </div>
  </div>
</template>
